export type AgentMode = 'portal-chat' | 'external-link' | 'planned';

export interface AgentEndpoints {
  chatUrl?: string;
  chatStreamUrl?: string;
  promptsUrl?: string;
  modelsUrl?: string;
}

export interface PromptMode {
  id: string;
  label: string;
  description?: string;
}

export interface ModelInfo {
  id: string;
  name: string;
  description?: string;
}

export interface AgentOptions {
  promptModes: PromptMode[];
  supportedModels: ModelInfo[];
  defaultModel: string | null;
}

/* --------------------------------------------------------------------------
   Chat types
   -------------------------------------------------------------------------- */

export type MessageRole = 'user' | 'assistant';

export interface ToolCall {
  id?: string;
  name: string;
  description: string;
  status: 'running' | 'success' | 'error';
  args?: unknown;
  /** Live-stream only — never persisted or replayed to the model. */
  result?: string;
  /** Live-stream only. */
  preview?: string;
}

export interface PatternCardData {
  title: string;
  fields: { label: string; value: string }[];
}

export type FeedbackRating = 'up' | 'down';

export interface MessageFeedback {
  rating: FeedbackRating;
  comment?: string;
  submittedAt: string;
}

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  agentId?: string;
  timestamp: string;
  toolCalls?: ToolCall[];
  patternCard?: PatternCardData;
  feedback?: MessageFeedback;
}

export interface Conversation {
  id: string;
  title: string;
  agentId: string;
  lastAgentId: string;
  lastMessageAt: string;
  messages: ChatMessage[];
}


export type SdlcGroup = 'plan' | 'code' | 'build' | 'test' | 'deploy' | 'operate' | 'secure';

export interface AgentDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  sdlcGroup: SdlcGroup;
  mode: AgentMode;
  toolCount?: number;
  capabilities?: string[];
  externalUrl?: string;
  endpoints?: AgentEndpoints;
}

export interface SdlcGroupMeta {
  label: string;
  icon: string;
  color: string;
  description: string;
}

export const SDLC_GROUP_META: Record<SdlcGroup, SdlcGroupMeta> = {
  plan: {
    label: 'Plan',
    icon: '📋',
    color: '#8b5cf6',
    description: 'Requirements, user stories, scaffolding plans',
  },
  code: {
    label: 'Code',
    icon: '💻',
    color: '#3b82f6',
    description: 'Code authoring, review, standards, docs',
  },
  build: {
    label: 'Build',
    icon: '🔨',
    color: '#10b981',
    description: 'Build, package, CI',
  },
  test: {
    label: 'Test',
    icon: '🧪',
    color: '#06b6d4',
    description: 'Unit, integration, QA automation',
  },
  deploy: {
    label: 'Deploy',
    icon: '🚀',
    color: '#f59e0b',
    description: 'Deployment, release orchestration',
  },
  operate: {
    label: 'Operate',
    icon: '🛡️',
    color: '#ef4444',
    description: 'Monitoring, incidents, SRE, cost',
  },
  secure: {
    label: 'Secure',
    icon: '🔐',
    color: '#dc2626',
    description: 'Security scanning, compliance, audit',
  },
};


